import React from 'react';

/**
 * DsButton — ボタンの正規実装（Web）。
 *
 * 新規・改修のボタンは原則これを使う。既存の共通CSS基盤（.btn / components.css）が
 * 付与するフラットな見た目・色ロール・状態（hover/disabled/タップ44px）をそのまま
 * 継承するため、bespoke な `xxx-btn` クラスを新設しない（＝デザインシステム順守）。
 *
 * variant: 'primary'(主・緑/既定) | 'secondary'(副・グレー) | 'danger'(危険・赤)
 *          | 'success'/'neutral'/'ghost'（既存 .btn 移行用 alias）
 * size:    'sm' | 'md'(既定) | 'lg'
 * block:   true で幅100%
 * icon:    true で正方形アイコンボタン（aria-label を必ず渡す）
 * loading: true で処理中（操作不可＋スピナー、aria-busy）
 *
 * type は既定 'button'（フォーム誤送信を防ぐ）。onClick / aria-* / form などの
 * 属性はそのまま下の要素へ委譲。`as` に 'a' 等を渡すとリンクとして同じ見た目で描画。
 *
 * 色・角丸・状態の正は docs/DEVELOPMENT.md（§2.5 早見表 / §2.8 部品ファースト）。
 */
const VARIANT_CLASS = {
  primary: 'btn--primary',
  secondary: 'btn--secondary',
  danger: 'btn--danger',
  success: 'btn--success',
  neutral: 'btn--neutral',
  ghost: 'btn--ghost',
};

const SIZE_CLASS = {
  sm: 'btn--sm',
  md: '',
  lg: 'btn--lg',
};

export default function DsButton({
  variant = 'primary',
  size = 'md',
  block = false,
  icon = false,
  loading = false,
  disabled = false,
  type = 'button',
  as: As = 'button',
  className = '',
  children,
  ...rest
}) {
  const classes = [
    'btn',
    VARIANT_CLASS[variant] || VARIANT_CLASS.primary,
    SIZE_CLASS[size] || '',
    block ? 'btn--block' : '',
    icon ? 'btn--icon' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const isDisabled = disabled || loading;

  // button のときだけ type/disabled を渡す。a 等では無効属性のため aria で表現。
  const elementProps =
    As === 'button'
      ? { type, disabled: isDisabled }
      : { 'aria-disabled': isDisabled || undefined };

  return (
    <As
      className={classes}
      data-loading={loading ? '' : undefined}
      aria-busy={loading || undefined}
      {...elementProps}
      {...rest}
    >
      {children}
    </As>
  );
}
